/**
 * SchoolOnlineExam — Timed Online Exam-Taking Interface
 * Timer, question navigation, auto-save, auto-submit
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { queueOfflineSubmit, syncOfflineSubmits, pendingOfflineCount } from '@/lib/examOfflineQueue';
import {
  Clock, CheckCircle2, ChevronLeft, ChevronRight, Send, Loader2,
  AlertTriangle, BookOpen, Play, Pause, Timer, Flag
} from 'lucide-react';
import TranslatePageButton from '@/components/TranslatePageButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable per-browser device ID — used by the backend single-device lock.
function getExamDeviceId() {
  let id = localStorage.getItem('exam_device_id');
  if (!id) {
    id = `dev_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem('exam_device_id', id);
  }
  return id;
}

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      'X-Exam-Device-Id': getExamDeviceId(),
      ...opts.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // FastAPI HTTPException can carry either a string or a dict (e.g. our
    // fee-gate payload with {gated, reason, message, outstanding_amount}).
    // Surface the human message when present.
    const d = err.detail;
    const msg = (d && typeof d === 'object') ? (d.message || d.reason || JSON.stringify(d)) : (d || 'Request failed');
    const error = new Error(msg);
    if (d && typeof d === 'object') error.payload = d;
    throw error;
  }
  return res.json();
};

const formatTime = (seconds) => {
  if (seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};


// ==================== EXAM TAKING UI ====================
const ExamTakingUI = ({ tenantId, examId, subjectId, session, onFinish }) => {
  const { t } = useTranslation();
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(session.remaining_seconds || 0);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [flagged, setFlagged] = useState(new Set());
  // Auto-save state — visible to the student so they always know whether their
  // last keystroke is safe on the server.
  //   'idle'   — nothing pending
  //   'saving' — POST in flight
  //   'saved'  — last save succeeded at savedAt
  //   'offline'— network failed; we are buffering in localStorage and will retry
  //   'failed' — server returned an error
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);
  const [isOnline, setIsOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const autoSaveRef = useRef(null);
  const debounceRef = useRef(null);
  const lastSavedJSONRef = useRef(null);
  const retryRef = useRef(null);
  const timerRef = useRef(null);
  // iter-396 — autosave hardening
  const clientVersionRef = useRef(0);            // increments per accepted save
  const autosaveStoppedRef = useRef(false);      // permanent stop flag
  const [autosaveStopped, setAutosaveStopped] = useState(null); // {code, message}
  // localStorage key for offline buffering — scoped per student session so
  // multiple tabs (same student, two devices) don't clobber each other.
  const lsKey = `exam-buffer:${tenantId}:${examId}:${subjectId}:${session.session_id || 'self'}`;

  const { data: examData, isLoading } = useQuery({
    queryKey: ['online-exam-questions', tenantId, examId, subjectId],
    queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/online/questions?subject_id=${subjectId}`),
    enabled: !!tenantId && !!examId && !!subjectId,
    refetchOnWindowFocus: false,
  });

  // Init saved answers — server-side takes precedence, then merge any
  // localStorage offline buffer that wasn't acknowledged before unload.
  useEffect(() => {
    if (!examData) return;
    let merged = { ...(examData.saved_answers || {}) };
    try {
      const buf = JSON.parse(localStorage.getItem(lsKey) || 'null');
      if (buf && buf.answers && typeof buf.answers === 'object') {
        // Local buffer wins for any keys not already saved on the server. This
        // is the resume-from-saved-point contract.
        merged = { ...buf.answers, ...merged };
        if (Object.keys(buf.answers).length > Object.keys(examData.saved_answers || {}).length) {
          // Local has unsynced answers → flag for resave below.
          lastSavedJSONRef.current = null;
        } else {
          lastSavedJSONRef.current = JSON.stringify(buf.answers);
        }
      } else {
        lastSavedJSONRef.current = JSON.stringify(examData.saved_answers || {});
      }
    } catch { /* ignore corrupted buffer */ }
    setAnswers(merged);
    if (examData.remaining_seconds) {
      setRemaining(examData.remaining_seconds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examData]);

  // Timer countdown
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Auto-save heartbeat every 15s (user choice 2.a)
  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      if (Object.keys(answers).length > 0) {
        saveProgress(false);
      }
    }, 15000);
    return () => clearInterval(autoSaveRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Save on tab blur / visibility change (anti-cheat already handles the
  // counter; this leg is purely about durability).
  useEffect(() => {
    const onBlur = () => { if (Object.keys(answers).length > 0) saveProgress(false); };
    const onVis = () => { if (document.visibilityState === 'hidden') onBlur(); };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Save before unload — best-effort sendBeacon so the browser doesn't
  // cancel the request when the tab closes. Also writes to localStorage
  // synchronously so a forced refresh / device crash still resumes.
  useEffect(() => {
    const onBeforeUnload = () => {
      try {
        localStorage.setItem(lsKey, JSON.stringify({ answers, ts: Date.now() }));
      } catch { /* quota / SecurityError → fine */ }
      // We avoid sendBeacon here because the save-progress endpoint expects
      // JSON + Authorization header which Beacon can't reliably attach.
      // The localStorage buffer + reconnect logic covers this case.
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Online / offline awareness — retry buffered save when we come back.
  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      if (saveState === 'offline' || saveState === 'failed') saveProgress(false);
    };
    const onOffline = () => { setIsOnline(false); setSaveState('offline'); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState]);

  // Tab-switch / focus-loss anti-cheat. Warn after 2, force-submit after 4.
  const tabSwitchCount = useRef(0);
  const [tabWarning, setTabWarning] = useState('');
  useEffect(() => {
    const onBlur = async () => {
      tabSwitchCount.current += 1;
      try {
        await api(`/tenants/${tenantId}/exams/${examId}/online/tab-switch?subject_id=${subjectId}`, { method: 'POST' });
      } catch { /* non-fatal */ }
      const n = tabSwitchCount.current;
      if (n === 2) {
        setTabWarning('Tab switch detected. One more and your exam will be auto-submitted.');
        toast.warning(t('exam_take.stay_focused'));
      } else if (n >= 4) {
        setTabWarning('Tab-switch limit exceeded. Exam auto-submitted.');
        handleAutoSubmit();
      }
    };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') onBlur();
    });
    return () => {
      window.removeEventListener('blur', onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMutation = useMutation({
    mutationFn: (answerList) => api(`/tenants/${tenantId}/exams/${examId}/online/save-progress`, {
      method: 'PUT',
      body: JSON.stringify({
        subject_id: subjectId,
        answers: answerList,
        client_version: clientVersionRef.current,
      }),
    }),
    onMutate: () => setSaveState('saving'),
    onSuccess: (data, vars) => {
      setSaveState('saved');
      setSavedAt(new Date());
      lastSavedJSONRef.current = JSON.stringify(answers);
      if (data && typeof data.server_version === 'number') {
        clientVersionRef.current = data.server_version;
      }
      // Server has it — local buffer no longer needed for that slice.
      try { localStorage.removeItem(lsKey); } catch { /* ignore */ }
      // schedule a fade back to idle after 5s so the indicator stays subtle
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => setSaveState((s) => s === 'saved' ? 'idle' : s), 5000);
    },
    onError: (e) => {
      // iter-396 — structured error_code drives the retry decision so a
      // permanent failure (session ended, two-tab conflict, taken-over device)
      // does NOT churn the network with hopeless retries.
      const payload = e?.payload || {};
      const code = payload.error_code;
      const PERMANENT = ['session_not_active', 'session_expired',
                          'device_taken_over', 'version_conflict'];
      if (PERMANENT.includes(code)) {
        autosaveStoppedRef.current = true;
        setAutosaveStopped({ code, message: payload.message || e.message });
        clearInterval(autoSaveRef.current);
        clearTimeout(retryRef.current);
        clearTimeout(debounceRef.current);
        setSaveState('failed');
        toast.error(payload.message || e.message);
        return;
      }
      // Transient failure — buffer locally + retry.
      try { localStorage.setItem(lsKey, JSON.stringify({ answers, ts: Date.now() })); } catch { /* ignore */ }
      setSaveState(navigator.onLine ? 'failed' : 'offline');
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => saveProgress(false), 8000);
    },
  });

  const submitMutation = useMutation({
    mutationFn: (answerList) => api(`/tenants/${tenantId}/exams/${examId}/online/submit`, {
      method: 'POST',
      body: JSON.stringify({ subject_id: subjectId, answers: answerList }),
    }),
    onSuccess: () => {
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
      try { localStorage.removeItem(lsKey); } catch { /* ignore */ }
      toast.success(t('exam_take.submitted_success'));
      onFinish();
    },
    onError: (e) => {
      // iter-369 — if we lost the network mid-submit, queue the submission
      // locally so it replays via /api/copilot/offline-submit once we're back.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const answerObj = Object.fromEntries(
          Object.entries(answers).map(([k, v]) => [k, v])
        );
        queueOfflineSubmit({
          tenant_id: tenantId, exam_id: examId, subject_id: subjectId,
          student_id: session?.student_id || '', class_id: session?.class_id || '',
          answers: answerObj, tab_switches: session?.tab_switch_count || 0,
          submitted_at_client: new Date().toISOString(),
        });
        toast.warning(t('exam_take.offline_saved'));
        clearInterval(timerRef.current);
        clearInterval(autoSaveRef.current);
        try { localStorage.removeItem(lsKey); } catch { /* ignore */ }
        onFinish();
        return;
      }
      toast.error(e.message);
    },
  });

  // iter-369 — try to drain the offline queue every time the device
  // comes back online (covers the case where a prior submission was
  // queued offline and we just regained connectivity).
  useEffect(() => {
    const onOnline = () => {
      syncOfflineSubmits().then((r) => {
        if (r.synced > 0) toast.success(`Synced ${r.synced} queued submission(s).`);
      }).catch(() => {});
    };
    window.addEventListener('online', onOnline);
    // Run one immediate sync at mount in case the user reloads after going back online.
    if (pendingOfflineCount() > 0) onOnline();
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const answersToList = useCallback(() => {
    return Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer }));
  }, [answers]);

  const saveProgress = useCallback((showToast = false) => {
    // iter-396 — never replay a save when the server has permanently refused
    // (session ended / two-tab conflict / device taken over).
    if (autosaveStoppedRef.current) return;
    const json = JSON.stringify(answers);
    // Skip no-op saves so we don't spam the server when the student is
    // navigating between questions without changing anything.
    if (json === lastSavedJSONRef.current && !showToast) return;
    const list = Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer }));
    saveMutation.mutate(list);
    if (showToast) toast.info('Progress saved');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Debounced save on every answer change (300 ms after the last keystroke).
  // Lives outside the answer state so we don't re-create the timeout per
  // render.
  useEffect(() => {
    if (Object.keys(answers).length === 0) return;
    // Also mirror to localStorage immediately so a crash between keystroke
    // and successful server save still resumes.
    try { localStorage.setItem(lsKey, JSON.stringify({ answers, ts: Date.now() })); } catch { /* ignore */ }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveProgress(false), 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  const handleAutoSubmit = () => {
    const list = Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer }));
    submitMutation.mutate(list);
  };

  const handleSubmit = () => {
    submitMutation.mutate(answersToList());
  };

  const updateAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const toggleFlag = (idx) => {
    setFlagged(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  }

  const questions = examData?.questions || [];
  const current = questions[currentQ];
  const answeredCount = questions.filter(q => answers[q.question_id]?.trim()).length;
  const isLowTime = remaining < 300;
  const isCriticalTime = remaining < 60;

  return (
    <div className="flex flex-col h-full min-h-[80vh]" data-testid="exam-taking-ui">
      {/* Tab-switch warning banner — anti-cheat */}
      {tabWarning && (
        <div className="bg-rose-100 border-b border-rose-300 px-3 py-2 text-xs text-rose-800 flex items-center gap-2" data-testid="exam-tab-warning">
          <AlertTriangle className="h-3.5 w-3.5" /> {tabWarning}
        </div>
      )}
      {/* iter-396 — permanent autosave-stopped banner.
          Tells the student in plain language that their inputs are no longer
          being saved and what to do about it. */}
      {autosaveStopped && (
        <div className="bg-amber-100 border-b-2 border-amber-400 px-3 py-2 text-sm text-amber-900 flex items-center gap-2"
              data-testid="exam-autosave-stopped">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div>
            <strong>Autosave stopped:</strong> {autosaveStopped.message}.{' '}
            {autosaveStopped.code === 'version_conflict'
              ? 'Another tab has newer answers — close this tab and continue in the other one.'
              : autosaveStopped.code === 'device_taken_over'
              ? 'You opened the exam on another device. Continue there.'
              : 'Please refresh — the exam window may have ended.'}
          </div>
        </div>
      )}
      {/* Timer Bar — marked translate="no" because the timer, save indicator
          and question counter all re-render on a sub-second cadence; Google
          Translate's DOM mutations on this churn region were the source of
          the React `removeChild` crash. Question text & options below are
          NOT marked, so they DO translate as the user expects. */}
      <div className={`sticky top-0 z-10 p-3 border-b flex items-center justify-between ${
        isCriticalTime ? 'bg-red-50 border-red-200' : isLowTime ? 'bg-amber-50 border-amber-200' : 'bg-white'
      }`} data-testid="exam-timer-bar" translate="no">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm">{t('exam_take.question_short')} {currentQ + 1}/{questions.length}</Badge>
          <span className="text-xs text-slate-500">
            {answeredCount}/{questions.length} {t('exam_take.answered')}
          </span>
          {/* Auto-save indicator — always visible so the student trusts the
              keystroke is durable. */}
          <span className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded ${
            saveState === 'saving' ? 'bg-slate-100 text-slate-600' :
            saveState === 'saved' ? 'bg-emerald-50 text-emerald-700' :
            saveState === 'offline' ? 'bg-amber-50 text-amber-700' :
            saveState === 'failed' ? 'bg-rose-50 text-rose-700' :
            'text-slate-400'
          }`} data-testid="exam-save-indicator" data-state={saveState}>
            {saveState === 'saving' && <><Loader2 className="h-3 w-3 animate-spin" /> {t('exam_take.saving')}</>}
            {saveState === 'saved' && savedAt && <><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t('exam_take.saved_at')} {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>}
            {saveState === 'offline' && <><span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> {t('exam_take.connection_lost')}</>}
            {saveState === 'failed' && <><span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" /> {t('exam_take.autosave_failed')}</>}
            {saveState === 'idle' && lastSavedJSONRef.current && <span className="opacity-60">{t('exam_take.all_saved')}</span>}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <TranslatePageButton size="sm" variant="ghost" />
          <Button size="sm" variant="ghost" onClick={() => saveProgress(true)} disabled={saveMutation.isPending} data-testid="exam-save-now-btn">
            {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : t('exam_take.save_now')}
          </Button>
          <div className={`flex items-center gap-1 font-mono text-lg font-bold ${
            isCriticalTime ? 'text-red-600 animate-pulse' : isLowTime ? 'text-amber-600' : 'text-slate-900'
          }`} data-testid="exam-timer">
            <Timer className="h-5 w-5" />
            {formatTime(remaining)}
          </div>
        </div>
      </div>

      <div className="flex flex-1 gap-4 p-4 overflow-hidden">
        {/* Question Navigation Panel — translate="no" because the grid
            re-renders whenever the student clicks a new question, which
            was triggering the Google-Translate / React reconciliation
            crash on language change. The labels here are just numbers. */}
        <div className="hidden sm:block w-20 shrink-0" translate="no">
          <div className="grid grid-cols-4 sm:grid-cols-2 gap-1.5" data-testid="question-nav-grid">
            {questions.map((q, idx) => {
              const isAnswered = !!answers[q.question_id]?.trim();
              const isFlagged = flagged.has(idx);
              const isCurrent = idx === currentQ;
              return (
                <button
                  key={q.question_id}
                  onClick={() => setCurrentQ(idx)}
                  className={`relative w-9 h-9 rounded-lg text-xs font-medium transition-all border
                    ${isCurrent ? 'bg-indigo-600 text-white border-indigo-600 ring-2 ring-indigo-300' :
                      isAnswered ? 'bg-green-100 text-green-700 border-green-300' :
                      'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  data-testid={`nav-q-${idx}`}
                >
                  {idx + 1}
                  {isFlagged && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Question Area */}
        <div className="flex-1 overflow-y-auto">
          {current && (
            <Card data-testid="current-question">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{current.question_type.replace('_', ' ')}</Badge>
                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">{current.marks} mark{current.marks !== 1 ? 's' : ''}</Badge>
                    <Badge variant="outline" className="text-xs capitalize">{current.difficulty}</Badge>
                  </div>
                  <Button
                    size="sm" variant={flagged.has(currentQ) ? 'default' : 'ghost'}
                    className={flagged.has(currentQ) ? 'bg-amber-500 hover:bg-amber-600' : ''}
                    onClick={() => toggleFlag(currentQ)}
                    data-testid="flag-question-btn"
                  >
                    <Flag className="h-3 w-3 mr-1" /> {flagged.has(currentQ) ? 'Flagged' : 'Flag'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm leading-relaxed font-medium" data-testid="question-text">{current.question_text}</p>

                {/* MCQ Single */}
                {current.question_type === 'mcq_single' && (
                  <div className="space-y-2">
                    {(current.options || []).map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => updateAnswer(current.question_id, opt)}
                        className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${
                          answers[current.question_id] === opt
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-900 ring-1 ring-indigo-200'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`option-${i}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {/* MCQ Multi */}
                {current.question_type === 'mcq_multi' && (
                  <div className="space-y-2">
                    {(current.options || []).map((opt, i) => {
                      const selected = (answers[current.question_id] || '').split(',').map(s => s.trim());
                      const isSelected = selected.includes(opt);
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            const newSelection = isSelected
                              ? selected.filter(s => s !== opt)
                              : [...selected.filter(s => s), opt];
                            updateAnswer(current.question_id, newSelection.join(','));
                          }}
                          className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${
                            isSelected
                              ? 'bg-indigo-50 border-indigo-300 text-indigo-900 ring-1 ring-indigo-200'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`option-${i}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                              {isSelected && <CheckCircle2 className="h-3 w-3 text-white" />}
                            </span>
                            {opt}
                          </span>
                        </button>
                      );
                    })}
                    <p className="text-xs text-slate-400">{t('exam_take.select_all_correct')}</p>
                  </div>
                )}

                {/* True/False */}
                {current.question_type === 'true_false' && (
                  <div className="flex gap-3">
                    {['True', 'False'].map(opt => (
                      <button
                        key={opt}
                        onClick={() => updateAnswer(current.question_id, opt)}
                        className={`flex-1 p-3 rounded-lg border text-center font-medium transition-all ${
                          answers[current.question_id] === opt
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-900 ring-1 ring-indigo-200'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`option-${opt.toLowerCase()}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {/* Short Answer */}
                {current.question_type === 'short_answer' && (
                  <Textarea
                    value={answers[current.question_id] || ''}
                    onChange={e => updateAnswer(current.question_id, e.target.value)}
                    placeholder={t('exam_take.type_answer')}
                    rows={3}
                    data-testid="short-answer-input"
                  />
                )}

                {/* Long Essay */}
                {current.question_type === 'long_essay' && (
                  <Textarea
                    value={answers[current.question_id] || ''}
                    onChange={e => updateAnswer(current.question_id, e.target.value)}
                    placeholder={t('exam_take.essay_placeholder')}
                    rows={8}
                    data-testid="essay-input"
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              disabled={currentQ === 0}
              onClick={() => setCurrentQ(prev => Math.max(0, prev - 1))}
              data-testid="prev-question-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>

            <div className="flex gap-2">
              {/* Mobile question nav */}
              <div className="sm:hidden flex gap-1 overflow-x-auto max-w-[150px]">
                {questions.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQ(idx)}
                    className={`w-7 h-7 rounded text-xs font-medium shrink-0 ${
                      idx === currentQ ? 'bg-indigo-600 text-white' :
                      answers[questions[idx]?.question_id]?.trim() ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-600'
                    }`}
                  >{idx + 1}</button>
                ))}
              </div>
            </div>

            {currentQ < questions.length - 1 ? (
              <Button onClick={() => setCurrentQ(prev => Math.min(questions.length - 1, prev + 1))} data-testid="next-question-btn">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={() => setShowSubmitConfirm(true)} className="bg-green-600 hover:bg-green-700" data-testid="finish-exam-btn">
                <Send className="h-4 w-4 mr-1" /> Submit Exam
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Submit Confirmation */}
      <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <DialogContent aria-describedby="submit-confirm-desc">
          <DialogHeader><DialogTitle>{t('exam_take.submit_question_title')}</DialogTitle></DialogHeader>
          <div className="space-y-3" id="submit-confirm-desc">
            <p className="text-sm">
              You have answered <strong>{answeredCount}</strong> of <strong>{questions.length}</strong> questions.
            </p>
            {answeredCount < questions.length && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {questions.length - answeredCount} question(s) unanswered
              </div>
            )}
            {flagged.size > 0 && (
              <p className="text-sm text-amber-600">{flagged.size} question(s) flagged for review</p>
            )}
            <p className="text-sm text-slate-500">Time remaining: <strong>{formatTime(remaining)}</strong></p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">{t('exam_take.review_answers')}</Button></DialogClose>
            <Button onClick={handleSubmit} disabled={submitMutation.isPending} className="bg-green-600 hover:bg-green-700" data-testid="confirm-submit-btn">
              {submitMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Submit Exam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== EXAM LAUNCHER ====================
export const OnlineExamLauncher = ({ tenantId, examId, subjectId, subjectName, examName, onClose }) => {
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [started, setStarted] = useState(false);

  const startMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${examId}/online/start`, {
      method: 'POST',
      body: JSON.stringify({ subject_id: subjectId }),
    }),
    onSuccess: (data) => {
      setSession(data);
      setStarted(true);
      if (data.resumed) toast.info(t('exam_take.resuming_session'));
      if (data.fee_warning?.warning) {
        toast.warning(data.fee_warning.message, { duration: 8000 });
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const { data: statusData } = useQuery({
    queryKey: ['online-exam-status', tenantId, examId, subjectId],
    queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/online/status?subject_id=${subjectId}`),
    enabled: !!tenantId && !!examId && !!subjectId,
  });

  if (started && session) {
    return (
      <ExamTakingUI
        tenantId={tenantId}
        examId={examId}
        subjectId={subjectId}
        session={session}
        onFinish={onClose}
      />
    );
  }

  const status = statusData?.status || 'not_started';

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="exam-launcher">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            {t('portal.my_exams', 'Online Exam')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t('exam_take.exam_label')}:</span><span className="font-medium">{examName}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t('exam_take.subject_label')}:</span><span className="font-medium">{subjectName}</span></div>
          </div>

          {status === 'submitted' && (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> {t('exam.submitted', 'You have already submitted this exam.')}
            </div>
          )}

          {status === 'expired' && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {t('exam.window_closed', 'Your exam session has expired.')}
            </div>
          )}

          {status === 'active' && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700 flex items-center gap-2">
              <Clock className="h-4 w-4" /> {t('exam_take.resuming_session', 'You have an active session. Click below to resume.')}
            </div>
          )}

          {status !== 'submitted' && status !== 'expired' && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {t('exam_take.instructions', 'Once started, the timer cannot be paused. Make sure you have a stable connection.')}
              </div>
              <Button
                className="w-full"
                onClick={() => startMutation.mutate()}
                disabled={startMutation.isPending}
                data-testid="start-exam-btn"
              >
                {startMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                {status === 'active' ? t('exam.take', 'Resume Exam') : t('exam_take.start_exam')}
              </Button>
            </>
          )}

          <Button variant="outline" className="w-full" onClick={onClose}>
            {t('common.back')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnlineExamLauncher;
